import { type ReactNode, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cn } from "../utils";

interface SheetProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  head?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  width?: "sm" | "md" | "lg" | "xl" | "2/3" | "full";
  flush?: boolean;
  portal?: Element | false;
}

export function Sheet({
  open,
  onClose,
  title,
  subtitle,
  head,
  children,
  footer,
  width = "2/3",
  flush = false,
  portal = document.body,
}: SheetProps) {
  const [visible, setVisible] = useState(false);
  const [animate, setAnimate] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(true);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimate(true));
      });
    } else {
      setAnimate(false);
      const timer = setTimeout(() => setVisible(false), 200);
      return () => clearTimeout(timer);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeydown);
    return () => document.removeEventListener("keydown", handleKeydown);
  }, [open, onClose]);

  if (!visible) return null;

  const widthClasses = {
    sm: "w-96",
    md: "w-[32rem]",
    lg: "w-[40rem]",
    xl: "w-[48rem]",
    "2/3": "w-2/3",
    full: "w-[calc(100%-3rem)]",
  };

  const content = (
    <div className="fixed inset-0 z-[9999]">
      {/* Backdrop */}
      <div
        className={cn(
          "fixed inset-0 bg-gray-500/75 cursor-pointer transition-opacity duration-200",
          animate ? "opacity-100" : "opacity-0"
        )}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        data-testid="sheet"
        role="dialog"
        aria-modal="true"
        className={cn(
          "fixed inset-y-0 right-0 z-50 h-screen bg-white shadow-xl flex flex-col",
          "divide-y divide-slate-200",
          "transition-transform duration-200 ease-in-out",
          widthClasses[width],
          animate ? "translate-x-0" : "translate-x-full"
        )}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
              {subtitle && (
                <p className="mt-1 text-sm text-slate-500">{subtitle}</p>
              )}
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-md text-slate-400 hover:text-slate-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-default-600"
            >
              <span className="sr-only">Close panel</span>
              <X className="size-6" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Optional head section */}
        {head && <div className="flex-shrink-0">{head}</div>}

        {/* Content */}
        <div className={cn("flex-1 min-h-0", flush ? "relative flex flex-col" : "overflow-y-auto px-6 py-6")}>{children}</div>

        {/* Optional footer */}
        {footer && (
          <div className="flex-shrink-0 px-6 py-4 bg-slate-50">{footer}</div>
        )}
      </div>
    </div>
  );

  return portal ? createPortal(content, portal) : content;
}
